/**
 * `subtasks` — `SubtasksValue`, the **column** only (`specs/M2-tier1-column-types.md` §5.9).
 * The nested subitems table and the subitems board are M3 S1.
 *
 * *"The subitems column is read-only — you cannot write values to it directly."* Use
 * `create_subitem` / `delete_item` `[09§A.7 subtasks]`. `value` and `text` were **null on all
 * 1,006 sampled cells** and null again in the sandbox recording, so `/v2` always serves null for
 * both while `subitems_ids` / `display_value` come from the materialised row (M1-B5 §5.5).
 *
 * `settings.boardIds[0]` is the "Subitems of X" board — the importer finds that board through
 * this key `[cvs§2 subtasks]`. `itemTypeName` is an i18n key, not a literal.
 *
 * M3.S1 note: the *Show Summary on Parent Item* toggle is **not** here. It is a per-column
 * setting on the **subitems board** `[uis:06-subitem-column-menu]`, so it never enters this
 * schema — `looseObject` still round-trips anything monday adds.
 */
import { z } from 'zod';
import { textShadow } from '../helpers.js';
import { defaultSummarySql } from '../sql.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const subtasksSettingsSchema = z.looseObject({
    allowMultipleItems: z.boolean().optional(),
    /** An i18n key (`column.subtasks.title`), not a literal `[cvs§2 subtasks]`. */
    itemTypeName: z.string().optional(),
    displayType: z.string().optional(),
    boardIds: z.array(z.union([z.number(), z.string()])).optional(),
});
import { subtasksFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** Our materialisation; monday itself stores nothing readable here. */
export const subtasksValueSchema = z.looseObject({
    subitems_ids: z.array(z.string()),
    display_value: z.string(),
});
export const DEFAULT_SUBTASKS_SETTINGS = {
    allowMultipleItems: true,
    itemTypeName: 'column.subtasks.title',
    displayType: 'BOARD_INLINE',
    boardIds: [],
};
/** The subitems board this column points at, or null when the column is not wired up yet. */
export function subitemsBoardId(settings) {
    const first = settings.boardIds?.[0];
    return first === undefined ? null : String(first);
}
export const subtasksColumn = defineColumnType({
    type: 'subtasks',
    title: 'Subitems',
    description: 'The item’s subitems, held on the board’s "Subitems of …" board.',
    apiFragmentName: 'SubtasksValue',
    legacyTypeName: 'subtasks',
    /**
     * Production carries both `subitems` and `subtasks` ids `[cvs§2 subtasks]`, but every column
     * monday **mints today** is `subtasks_<8 base36>` — measured on three boards in one session
     * (`subtasks_mm71b4hv`, `subtasks_mm716rc7`, `subtasks_mm72evy6`; `[sub:21]`, `[sub:31]`,
     * `[sub:37]`). Imported columns keep their id verbatim either way (M1-B5 §5.6 rule 1).
     */
    idPrefix: 'subtasks',
    valueSchema: subtasksValueSchema,
    settingsSchema: subtasksSettingsSchema,
    defaultSettings: DEFAULT_SUBTASKS_SETTINGS,
    settingsStrSchema: subtasksSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    /** monday's support is unrecorded; the conservative reading offers nothing (Q66). */
    filterConditions: subtasksFilterConditions,
    filterOperators: operatorsOf(subtasksFilterConditions),
    aggregations: ['count', 'none'],
    /** `text` is null on every sampled cell, populated or not `[cvs§2 subtasks]`. */
    emptyText: null,
    computed: {
        dependsOn() {
            return [{ kind: 'subitems' }];
        },
        compute(ctx) {
            const subitems = ctx.subitems ?? [];
            if (subitems.length === 0)
                return null;
            return {
                subitems_ids: subitems.map((subitem) => subitem.id),
                display_value: subitems.map((subitem) => subitem.name).join(', '),
            };
        },
        materialise: 'always',
        api: { value: 'null', text: 'null' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('subtasks', 'the subitems column is read-only');
    },
    toText() {
        // `/v2` serves null for populated cells too `[cvs§2 subtasks]`.
        return null;
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        return {
            text: textShadow(value.display_value),
            number: value.subitems_ids.length,
            date: null,
        };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        const left = a.subitems_ids;
        const right = b.subitems_ids;
        return left.length === right.length && left.every((id, index) => id === right[index]);
    },
    toActivityValue() {
        return null;
    },
    fromMondayValue() {
        // Derived: never imported, always recomputed `[cvs§4.6]`.
        return null;
    },
    toMondayValue() {
        return null;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return subtasksSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value) {
        return {
            display_value: value?.display_value ?? '',
            subitems_ids: value?.subitems_ids ?? [],
        };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    filterSql() {
        return null;
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map